import React, { useEffect } from 'react';
import useSEO from '../hooks/useSEO';

// Texto plano de cada pregunta/respuesta, en el mismo orden que el JSX de
// abajo -- se mantienen separados (en vez de generar el JSX a partir de
// este array) porque un par de respuestas llevan formato/links que no
// tiene sentido meter en un schema de texto plano.
const FAQ_ITEMS = [
  {
    question: '¿Cómo personalizo mi estampita?',
    answer:
      'Elegí un diseño de la colección, seleccioná la cantidad de copias y completá el nombre, la fecha, la oración y los demás datos del niño/a. Vas a poder ver una previsualización antes de confirmar el pedido.',
  },
  {
    question: '¿Qué medidas y papel tienen las estampitas?',
    answer:
      'Las estampitas miden 8 x 13 cm y se imprimen en papel de 250 gramos con terminación mate. Elegimos papeles respetuosos con el medio ambiente para cuidar cada detalle de tu recuerdo.',
  },
  {
    question: '¿Cuánto demora la entrega?',
    answer:
      'Los plazos de producción y envío pueden variar según la demanda y la zona de entrega. Te avisaremos por email cualquier cambio relevante sobre tu pedido.',
  },
  {
    question: '¿Qué métodos de pago aceptan?',
    answer:
      'Procesamos los pagos a través de Mercado Pago, que admite tarjetas de débito, crédito y otros medios de pago habilitados en la plataforma.',
  },
  {
    question: '¿Puedo hacer cambios o devoluciones?',
    answer:
      'Al ser un producto personalizado, no se aceptan devoluciones salvo que haya un error en los datos por parte nuestra. Revisá bien los datos en la previsualización antes de confirmar tu pedido.',
  },
  {
    question: '¿A dónde envían los pedidos?',
    answer:
      'Envíos a Montevideo y a todo el interior por Agencia Central (DAC) y Copay. El costo de envío se calcula y muestra antes de confirmar la compra.',
  },
  {
    question: '¿Cómo los contacto?',
    answer: 'Podés escribirnos a hola@miestampita.com o seguirnos en Instagram (@miestampitacom).',
  },
];

// Inyecta el schema FAQPage para que Google pueda mostrar las preguntas
// desplegables directo en el resultado de búsqueda. Se saca del <head> al
// desmontar para no dejarlo pisando el de otra página.
const useFaqStructuredData = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map(({ question, answer }) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answer,
        },
      })),
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);
};

export default function Faq() {
  useSEO({
    title: 'Preguntas frecuentes | MiEstampita',
    description:
      'Resolvé tus dudas sobre cómo personalizar tu estampita, medidas y papel, tiempos de producción, envíos y devoluciones.',
    path: '/faq',
  });
  useFaqStructuredData();

  return (
    <main className="terms-page">
      <section className="terms-page__content">
        <h1>Preguntas frecuentes</h1>

        <h2>¿Cómo personalizo mi estampita?</h2>
        <p>
          Elegí un diseño de la colección, seleccioná la cantidad de copias y completá el
          nombre, la fecha, la oración y los demás datos del niño/a. Vas a poder ver una
          previsualización antes de confirmar el pedido.
        </p>

        <h2>¿Qué medidas y papel tienen las estampitas?</h2>
        <p>
          Las estampitas miden 8 x 13 cm y se imprimen en papel de 250 gramos con
          terminación mate. Elegimos papeles respetuosos con el medio ambiente para cuidar
          cada detalle de tu recuerdo.
        </p>

        <h2>¿Cuánto demora la entrega?</h2>
        <p>
          Los plazos de producción y envío pueden variar según la demanda y la zona de
          entrega. Te avisaremos por email cualquier cambio relevante sobre tu pedido.
        </p>

        <h2>¿Qué métodos de pago aceptan?</h2>
        <p>
          Procesamos los pagos a través de Mercado Pago, que admite tarjetas de débito,
          crédito y otros medios de pago habilitados en la plataforma.
        </p>

        <h2>¿Puedo hacer cambios o devoluciones?</h2>
        <p>
          Al ser un producto personalizado,{' '}
          <em style={{ color: '#819b78' }}>no se aceptan devoluciones</em> salvo que haya
          un error en los datos por parte nuestra. Revisá bien los datos en la previsualización
          antes de confirmar tu pedido.
        </p>

        <h2>¿A dónde envían los pedidos?</h2>
        <p>
          Envíos a Montevideo y a todo el interior por Agencia Central (DAC) y Copay. El costo
          de envío se calcula y muestra antes de confirmar la compra.
        </p>

        <h2>¿Cómo los contacto?</h2>
        <p>
          Podés escribirnos a{' '}
          <a href="mailto:hola@miestampita.com">hola@miestampita.com</a> o seguirnos en{' '}
          <a
            href="https://www.instagram.com/miestampitacom"
            target="_blank"
            rel="noopener noreferrer"
          >
            Instagram
          </a>
          .
        </p>
      </section>
    </main>
  );
}
